import { isCancelledOrLateCancellationStatus } from './attendanceDisplay';

export const SPECIALIZATION_TO_SERVICE = {
  'Speech Therapy': 'ST',
  'Occupational Therapy': 'OT',
  'Physical Therapy': 'PT',
  'Play Group': 'PG',
};

export const SLOT_SOURCES = {
  OPEN: 'open',
  CANCELLED: 'cancelled',
  LUNCH: 'lunch',
};

export const SLOT_SOURCE_LABELS = {
  [SLOT_SOURCES.OPEN]: 'Open slot',
  [SLOT_SOURCES.CANCELLED]: 'Cancelled session',
  [SLOT_SOURCES.LUNCH]: 'Lunch break',
};

export function normalizeTimeSlot(time) {
  const hour = parseInt(String(time).split(':')[0], 10);
  return Number.isNaN(hour) ? String(time) : `${hour}:00`;
}

export function parseHour(time) {
  return parseInt(String(time).split(':')[0], 10);
}

export function sessionValidOnDate(session, dateStr) {
  const validUntil = session.valid_until || '9999-12-31';
  return dateStr >= session.valid_from && dateStr <= validUntil;
}

export function buildAttendanceMap(attendanceRecords = []) {
  const map = {};
  attendanceRecords.forEach((record) => {
    map[`${record.session_id}_${record.attendance_date}`] = record.status;
  });
  return map;
}

export function sessionOccupiesSlot(session, slotDate, slotDay, slotTime, attendanceMap) {
  if (!sessionValidOnDate(session, slotDate)) return false;
  if (session.day !== slotDay) return false;
  if (normalizeTimeSlot(session.time) !== normalizeTimeSlot(slotTime)) return false;

  const status = attendanceMap[`${session.id}_${slotDate}`];
  if (!status) return true;
  return !isCancelledOrLateCancellationStatus(status);
}

export function clientOccupiesHour(clientSessions, attendanceMap, dateStr, dayName, hour) {
  const slotTime = `${hour}:00`;

  return clientSessions.some((session) =>
    sessionOccupiesSlot(session, dateStr, dayName, slotTime, attendanceMap)
  );
}

export function getClientGapRelation(clientSessions, attendanceMap, dateStr, dayName, slotHour) {
  if (clientOccupiesHour(clientSessions, attendanceMap, dateStr, dayName, slotHour)) {
    return null;
  }

  const hasBefore = clientOccupiesHour(clientSessions, attendanceMap, dateStr, dayName, slotHour - 1);
  const hasAfter = clientOccupiesHour(clientSessions, attendanceMap, dateStr, dayName, slotHour + 1);

  if (hasBefore && !hasAfter) return 'after';
  if (hasAfter && !hasBefore) return 'before';
  if (hasBefore && hasAfter) return 'between';
  return null;
}

export function isPendingMakeupRecord(record) {
  return (
    record.madeup_for === false &&
    record.makeup_scheduled === false &&
    !record.paid_noshow &&
    isCancelledOrLateCancellationStatus(record.status)
  );
}

export function therapistHasLunchBreak(scheduleSlots = []) {
  if (!scheduleSlots.length) return false;
  const normalized = scheduleSlots.map(normalizeTimeSlot);
  if (normalized.includes('12:00')) return false;
  return normalized.includes('11:00') || normalized.includes('13:00');
}

export function dedupeSlots(slots) {
  const seen = new Set();
  return slots.filter((slot) => {
    const key = `${slot.therapistId}_${normalizeTimeSlot(slot.time)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function therapistHasScheduledClientOnDate(
  therapistId,
  sessions,
  attendanceMap,
  dateStr,
  dayName
) {
  return sessions.some((session) => {
    if (session.therapist_id !== therapistId) return false;
    if (!sessionValidOnDate(session, dateStr)) return false;
    if (session.day !== dayName) return false;
    if (session.is_makeup) return false;

    const status = attendanceMap[`${session.id}_${dateStr}`];
    if (!status) return true;
    return !isCancelledOrLateCancellationStatus(status);
  });
}

export function buildAvailableTherapistSlots({
  dateStr,
  dayName,
  therapists = [],
  sessions = [],
  attendanceMap = {},
  openAvailability = [],
}) {
  const slots = [];

  openAvailability.forEach((row) => {
    const therapist = therapists.find((t) => t.id === row.therapist_id);
    if (!therapist) return;

    const occupied = sessions.some(
      (session) =>
        session.therapist_id === row.therapist_id &&
        sessionOccupiesSlot(session, dateStr, dayName, row.time_slot, attendanceMap)
    );

    if (!occupied) {
      slots.push({
        therapistId: therapist.id,
        therapist,
        time: normalizeTimeSlot(row.time_slot),
        source: SLOT_SOURCES.OPEN,
      });
    }
  });

  sessions.forEach((session) => {
    if (session.therapist_id == null) return;
    if (!sessionValidOnDate(session, dateStr)) return;
    if (session.day !== dayName) return;
    if (session.is_makeup) return;

    const status = attendanceMap[`${session.id}_${dateStr}`];
    if (!isCancelledOrLateCancellationStatus(status)) return;

    const therapist = therapists.find((t) => t.id === session.therapist_id);
    if (!therapist) return;

    slots.push({
      therapistId: therapist.id,
      therapist,
      time: normalizeTimeSlot(session.time),
      source: SLOT_SOURCES.CANCELLED,
    });
  });

  therapists.forEach((therapist) => {
    const scheduleSlots = therapist.schedule?.[dayName] || [];
    if (!therapistHasLunchBreak(scheduleSlots)) return;

    const lunchTime = '12:00';
    const occupied = sessions.some(
      (session) =>
        session.therapist_id === therapist.id &&
        sessionOccupiesSlot(session, dateStr, dayName, lunchTime, attendanceMap)
    );

    if (!occupied) {
      slots.push({
        therapistId: therapist.id,
        therapist,
        time: lunchTime,
        source: SLOT_SOURCES.LUNCH,
      });
    }
  });

  const therapistsWithClients = new Set(
    therapists
      .filter((therapist) =>
        therapistHasScheduledClientOnDate(
          therapist.id,
          sessions,
          attendanceMap,
          dateStr,
          dayName
        )
      )
      .map((therapist) => therapist.id)
  );

  return dedupeSlots(slots)
    .filter((slot) => therapistsWithClients.has(slot.therapistId))
    .sort(
    (a, b) => parseHour(a.time) - parseHour(b.time) || a.therapist.name.localeCompare(b.therapist.name)
  );
}

export function getClientOccupiedHours(clientSessions, attendanceMap, dateStr, dayName, minHour = 8, maxHour = 18) {
  const occupiedHours = [];

  for (let hour = minHour; hour <= maxHour; hour += 1) {
    if (clientOccupiesHour(clientSessions, attendanceMap, dateStr, dayName, hour)) {
      occupiedHours.push(hour);
    }
  }

  return occupiedHours;
}

export function clientHasConsecutiveSessions(
  clientSessions,
  attendanceMap,
  dateStr,
  dayName,
  consecutiveHours = 2
) {
  const occupiedHours = getClientOccupiedHours(clientSessions, attendanceMap, dateStr, dayName);
  if (occupiedHours.length < consecutiveHours) return false;

  let streak = 1;
  for (let i = 1; i < occupiedHours.length; i += 1) {
    if (occupiedHours[i] === occupiedHours[i - 1] + 1) {
      streak += 1;
      if (streak >= consecutiveHours) return true;
    } else {
      streak = 1;
    }
  }

  return false;
}

export function pickFarthestPendingMakeupByClient(pendingMakeups = [], referenceDate = new Date()) {
  const referenceTime = referenceDate.getTime();
  const byClient = {};

  pendingMakeups.forEach((pending) => {
    const clientId = pending.client?.id || pending.client_id;
    if (!clientId) return;

    const pendingTime = new Date(pending.attendance_date).getTime();
    const pendingDistance = Math.abs(referenceTime - pendingTime);

    if (!byClient[clientId]) {
      byClient[clientId] = { pending, distance: pendingDistance };
      return;
    }

    if (pendingDistance > byClient[clientId].distance) {
      byClient[clientId] = { pending, distance: pendingDistance };
    }
  });

  return Object.values(byClient).map((entry) => entry.pending);
}

export function clientHasScheduledMakeupForTherapyType(
  clientSessions,
  attendanceMap,
  dateStr,
  dayName,
  specialization,
  therapistsById
) {
  if (!specialization) return false;

  return clientSessions.some((session) => {
    if (!session.is_makeup) return false;
    if (!sessionValidOnDate(session, dateStr)) return false;
    if (session.day !== dayName) return false;

    const status = attendanceMap[`${session.id}_${dateStr}`];
    if (status && isCancelledOrLateCancellationStatus(status)) return false;

    const sessionSpecialization = therapistsById.get(session.therapist_id);
    return sessionSpecialization === specialization;
  });
}

export function buildMakeupOpportunities({
  dateStr,
  dayName,
  availableSlots = [],
  pendingMakeups = [],
  clientSessions = [],
  attendanceMap = {},
  referenceDate = new Date(),
  therapists = [],
}) {
  const therapistsById = new Map(therapists.map((therapist) => [therapist.id, therapist.specialization]));
  const sessionsByClient = clientSessions.reduce((acc, session) => {
    if (!session.client_id) return acc;
    if (!acc[session.client_id]) acc[session.client_id] = [];
    acc[session.client_id].push(session);
    return acc;
  }, {});

  const eligiblePendingMakeups = pickFarthestPendingMakeupByClient(pendingMakeups, referenceDate);

  const opportunities = [];

  availableSlots.forEach((slot) => {
    const slotHour = parseHour(slot.time);
    const matches = [];

    eligiblePendingMakeups.forEach((pending) => {
      const clientId = pending.client?.id || pending.client_id;
      if (!clientId) return;
      if (slot.therapist.specialization !== pending.therapist?.specialization) return;

      const clientSessionList = sessionsByClient[clientId] || [];

      if (clientHasConsecutiveSessions(clientSessionList, attendanceMap, dateStr, dayName)) {
        return;
      }

      const therapyType = pending.therapist?.specialization;
      if (
        clientHasScheduledMakeupForTherapyType(
          clientSessionList,
          attendanceMap,
          dateStr,
          dayName,
          therapyType,
          therapistsById
        )
      ) {
        return;
      }

      const gap = getClientGapRelation(
        clientSessionList,
        attendanceMap,
        dateStr,
        dayName,
        slotHour
      );

      if (!gap) return;

      const originalTherapistId = pending.therapist_id || pending.therapist?.id;
      const sameOriginalTherapist = originalTherapistId === slot.therapistId;

      matches.push({
        pendingMakeup: pending,
        client: pending.client,
        gap,
        adjacentHour: gap === 'before' ? slotHour + 1 : gap === 'after' ? slotHour - 1 : null,
        sameOriginalTherapist,
      });
    });

    if (matches.length > 0) {
      matches.sort((a, b) => {
        if (a.sameOriginalTherapist !== b.sameOriginalTherapist) {
          return a.sameOriginalTherapist ? -1 : 1;
        }
        return (a.client?.name || '').localeCompare(b.client?.name || '');
      });
      opportunities.push({ slot, matches });
    }
  });

  return opportunities;
}

export function filterFutureSlotsForToday(slots, dateStr, now = new Date()) {
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  if (dateStr !== todayStr) return slots;

  const currentHour = now.getHours();
  return slots.filter((slot) => parseHour(slot.time) > currentHour);
}

/** Date ascending, then hour ascending (avoids string sort where "8:00" follows "16:00"). */
export function compareMakeupAvailabilitySlots(a, b) {
  const dateCmp = String(a?.date ?? '').localeCompare(String(b?.date ?? ''));
  if (dateCmp !== 0) return dateCmp;
  return parseHour(a?.time_slot) - parseHour(b?.time_slot);
}

export function sortMakeupAvailabilitySlots(slots = []) {
  return [...slots].sort(compareMakeupAvailabilitySlots);
}
